import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { workPlanApi, taskApi } from '../../services/api';
import { Button } from '../ui/button';
import { TextField } from '../ui/TextField';
import { TextArea } from '../ui/TextArea';
import { Badge } from '../ui/badge';
import { ProjectBadge } from '../ui/project-badge';
import PageHeader from '../Layout/PageHeader';
import TaskTypeIcon from '../shared/TaskTypeIcon';
import TaskDueDate from '../shared/TaskDueDate';
import { FeatureTag } from '../ui/feature-tag';
import { Filter, Search, ChevronDown, ChevronUp, Info, Paperclip } from 'react-feather';
import TaskSummaryModal from '../../pages/components/TaskSummaryModal';
import api from '../../services/api';
import { isManager } from '../../utils/roleUtils';
import TaskTypeSelect, { TASK_TYPES } from '../shared/TaskTypeSelect';
import './AddToWorkPlan.css';

const TASK_STATUS = {
  OPEN: 'open',
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed',
  CLOSED: 'closed',
  RELEASED: 'released'
};

const NON_SELECTABLE_STATUSES = [TASK_STATUS.COMPLETED, TASK_STATUS.CLOSED, TASK_STATUS.RELEASED];

const AddToWorkPlan = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mode, setMode] = useState('select');
  const [allTasks, setAllTasks] = useState([]);
  const [availableTasks, setAvailableTasks] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [newTask, setNewTask] = useState({ title: '', description: '' });
  const [activeWorkPlan, setActiveWorkPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);
  const [filters, setFilters] = useState({
    searchQuery: '',
    status: [],
    project: '',
    taskType: '',
    featureTag: ''
  });
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [selectedTaskForModal, setSelectedTaskForModal] = useState(null);
  const [userIsManager, setUserIsManager] = useState(false);
  const statusDropdownRef = React.useRef(null);

  useEffect(() => {
    loadWorkPlanAndTasks();
    checkUserRole();
  }, []);

  const checkUserRole = async () => {
    try {
      const response = await api.get('/api/auth/role');
      const userRole = response.data?.role;
      setUserIsManager(isManager(userRole));
    } catch (err) {
      console.error('Error checking user role:', err);
    }
  };

  const loadWorkPlanAndTasks = async () => {
    try {
      // Load active work plan and all tasks in parallel
      const [activePlan, allTasks] = await Promise.all([
        workPlanApi.getActive(),
        taskApi.getAll()
      ]);

      if (!activePlan) {
        navigate('/dashboard');
        return;
      }

      // Check if the work day is closed
      if (activePlan.isSubmitted || activePlan.isClosed) {
        navigate('/dashboard');
        return;
      }

      setActiveWorkPlan(activePlan);

      // Filter out completed tasks and tasks already in today's plan
      const filteredTasks = allTasks.filter(task => 
        task.status !== TASK_STATUS.COMPLETED &&
        !activePlan.tasks.some(planTask => planTask.task_id === task.id)
      );

      setAllTasks(filteredTasks);
      setAvailableTasks(filteredTasks);
    } catch (error) {
      console.error('Failed to load work plan and tasks:', error);
      setError('Failed to load work plan and tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTask = async () => {
    if (!activeWorkPlan) return;

    try {
      let taskToAdd;
      if (mode === 'select' && selectedTaskId) {
        taskToAdd = availableTasks.find(task => task.id === selectedTaskId);
        if (!taskToAdd) return;
      } else if (mode === 'create' && newTask.title.trim()) {
        // Create a new task with OPEN status
        const createdTask = await taskApi.create({
          title: newTask.title.trim(),
          description: newTask.description.trim(),
          status: TASK_STATUS.OPEN.toLowerCase(),
          user_id: user.id
        });
        taskToAdd = createdTask;
      } else {
        return;
      }

      // Add task to work plan using the API endpoint
      await workPlanApi.addTasks(activeWorkPlan.id, [taskToAdd]);

      // Navigate back to dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to add task to work plan:', error);
      setError('Failed to add task to work plan');
    }
  };

  const isTaskSelectable = (task) => {
    return !NON_SELECTABLE_STATUSES.includes(task.status);
  };

  // Get unique values for filter options
  const filterOptions = useMemo(() => {
    const projects = new Set();
    const taskTypes = new Set();
    const featureTags = new Set();
    
    allTasks.forEach(task => {
      if (task.project?.id) {
        projects.add(JSON.stringify({ id: task.project.id, name: task.project.name }));
      }
      if (task.task_type) {
        taskTypes.add(task.task_type);
      }
      if (task.feature_tag?.id) {
        featureTags.add(JSON.stringify({ id: task.feature_tag.id, name: task.feature_tag.name }));
      }
    });

    return {
      projects: Array.from(projects).map(p => JSON.parse(p)),
      taskTypes: Array.from(taskTypes).sort((a, b) => {
        const idxA = TASK_TYPES.indexOf(a);
        const idxB = TASK_TYPES.indexOf(b);
        return (idxA === -1 ? TASK_TYPES.length : idxA) - (idxB === -1 ? TASK_TYPES.length : idxB);
      }),
      featureTags: Array.from(featureTags).map(t => JSON.parse(t))
    };
  }, [allTasks]);

  // Filter available tasks based on filter criteria
  const filteredAvailableTasks = useMemo(() => {
    let filtered = allTasks;

    // Apply search query
    if (filters.searchQuery.trim()) {
      const query = filters.searchQuery.toLowerCase().trim();
      filtered = filtered.filter(task => {
        const title = (task.title || '').toLowerCase();
        const description = (task.description || '').toLowerCase();
        const displayId = (task.display_id || '').toLowerCase();
        return title.includes(query) || description.includes(query) || displayId.includes(query);
      });
    }

    // Apply status filter
    if (filters.status && filters.status.length > 0) {
      filtered = filtered.filter(task => filters.status.includes(task.status));
    }

    // Apply project filter
    if (filters.project) {
      filtered = filtered.filter(task => task.project?.id === filters.project);
    }

    // Apply task type filter
    if (filters.taskType) {
      filtered = filtered.filter(task => (task.task_type || 'Task') === filters.taskType);
    }

    // Apply feature tag filter
    if (filters.featureTag) {
      if (filters.featureTag === 'untagged') {
        filtered = filtered.filter(task => !task.feature_tag?.id);
      } else {
        filtered = filtered.filter(task => task.feature_tag?.id === filters.featureTag);
      }
    }

    return filtered;
  }, [allTasks, filters]);

  // Update available tasks when filters change
  useEffect(() => {
    setAvailableTasks(filteredAvailableTasks);
  }, [filteredAvailableTasks]);

  const handleStatusToggle = (status) => {
    setFilters(prev => {
      const currentStatuses = prev.status || [];
      const newStatuses = currentStatuses.includes(status)
        ? currentStatuses.filter(s => s !== status)
        : [...currentStatuses, status];
      return { ...prev, status: newStatuses };
    });
  };

  const clearFilters = () => {
    setFilters({
      searchQuery: '',
      status: [],
      project: '',
      taskType: '',
      featureTag: ''
    });
    setStatusDropdownOpen(false);
  };

  const statusOptions = [
    { value: TASK_STATUS.OPEN, label: 'Open' },
    { value: TASK_STATUS.IN_PROGRESS, label: 'In Progress' }
  ];

  // Close status dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setStatusDropdownOpen(false);
      }
    };

    if (statusDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [statusDropdownOpen]);

  // Helper function to calculate task summary data
  const calculateTaskSummary = useCallback((task) => {
    if (!task.work_plan_tasks || task.work_plan_tasks.length === 0) {
      return {
        totalHours: 0,
        occurrences: 0,
        work_plan_attachments: []
      };
    }

    const totalHours = task.work_plan_tasks.reduce((sum, wpt) => sum + (parseFloat(wpt.hours) || 0), 0);
    const occurrences = task.work_plan_tasks.length;
    
    // Collect all attachments from work plan tasks
    const attachments = [];
    task.work_plan_tasks.forEach(wpt => {
      if (wpt.work_plan_attachments && wpt.work_plan_attachments.length > 0) {
        attachments.push(...wpt.work_plan_attachments);
      }
    });

    return {
      totalHours: totalHours.toFixed(1),
      occurrences,
      work_plan_attachments: attachments
    };
  }, []);

  // Function to open task summary modal
  const openTaskSummaryModal = useCallback(async (task) => {
    try {
      // Fetch full task details with work_plan_tasks
      const fullTask = await taskApi.get(task.id);
      const summary = calculateTaskSummary(fullTask);
      setSelectedTaskForModal({
        ...fullTask,
        ...summary
      });
    } catch (error) {
      console.error('Failed to fetch task details:', error);
      // Fallback to using the task we have
      const summary = calculateTaskSummary(task);
      setSelectedTaskForModal({
        ...task,
        ...summary
      });
    }
  }, [calculateTaskSummary]);

  // Function to close task summary modal
  const closeTaskSummaryModal = useCallback(() => {
    setSelectedTaskForModal(null);
  }, []);

  // Helper function to get task status
  const getTaskStatus = useCallback((taskId) => {
    const task = allTasks.find(t => t.id === taskId) || selectedTaskForModal;
    return task?.status || 'open';
  }, [allTasks, selectedTaskForModal]);

  // Helper function to get status variant for badge
  const getStatusVariant = useCallback((status) => {
    switch (status?.toLowerCase()) {
      case 'in-progress':
        return 'warning';
      case 'completed':
        return 'success';
      case 'released':
        return 'success';
      case 'open':
        return 'neutral';
      default:
        return 'neutral';
    }
  }, []);

  // Helper function to get task history from work_plan_tasks
  const getTaskHistory = useCallback((taskId) => {
    const task = selectedTaskForModal || allTasks.find(t => t.id === taskId);
    if (!task || !task.work_plan_tasks || task.work_plan_tasks.length === 0) {
      return [];
    }

    return task.work_plan_tasks
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .map(wpt => {
        const dateObj = new Date(wpt.created_at);
        return {
          date: dateObj,
          hours: wpt.hours || 0,
          notes: wpt.notes || '',
          status: wpt.status === 'completed' ? 'Completed' : 'In Progress',
          attachments: wpt.work_plan_attachments || []
        };
      });
  }, [allTasks, selectedTaskForModal]);

  if (loading) return (
    <div className="flex w-full flex-col gap-6 px-6 py-6">
      <PageHeader title="Add to Work Plan" />
      <div className="loading-container">Loading...</div>
    </div>
  );

  if (error) return (
    <div className="flex w-full flex-col gap-6 px-6 py-6">
      <PageHeader title="Add to Work Plan" />
      <div className="error-message">{error}</div>
    </div>
  );

  return (
    <div className="flex w-full flex-col gap-6 px-6 py-6">
      <PageHeader title="Add to Work Plan" />

      <div className="add-to-workplan-container">
        <div className="mode-selector">
          <Button 
            variant={mode === 'select' ? 'primary' : 'neutral-tertiary'}
            onClick={() => setMode('select')}
            className="mode-button"
          >
            Select Existing Task
          </Button>
          <Button 
            variant={mode === 'create' ? 'primary' : 'neutral-tertiary'}
            onClick={() => setMode('create')}
            className="mode-button"
          >
            Create New Task
          </Button>
        </div>

        {mode === 'select' ? (
          <div className="select-task-section">
            <h3 className="section-title">Select a Task</h3>
            
            {/* Advanced Filters */}
            <div className="task-controls-section">
              <div className="task-controls-header" onClick={() => setFiltersCollapsed(!filtersCollapsed)} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                  <h3 className="task-controls-title">
                    <Filter size={16} />
                    Advanced Filters
                  </h3>
                </div>
                <button 
                  className="stats-toggle-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFiltersCollapsed(!filtersCollapsed);
                  }}
                  aria-label={filtersCollapsed ? "Expand filters" : "Collapse filters"}
                >
                  {filtersCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                </button>
              </div>
              <div className={`task-controls task-controls-compact ${filtersCollapsed ? 'collapsed' : ''}`}>
                <div className="filter-controls-compact-wrapper">
                  {/* Search Box */}
                  <div className="task-search-section-compact">
                    <div className="task-search-wrapper-compact">
                      <Search size={14} className="task-search-icon-compact" />
                      <input
                        type="text"
                        className="task-search-input-compact"
                        placeholder="Search..."
                        value={filters.searchQuery}
                        onChange={(e) => setFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
                      />
                      {filters.searchQuery && (
                        <button
                          className="task-search-clear-compact"
                          onClick={() => setFilters(prev => ({ ...prev, searchQuery: '' }))}
                          title="Clear search"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="filter-controls-compact">
                    {/* Status Filter */}
                    <div className="filter-group-compact">
                      <label htmlFor="filter-status" className="filter-label-compact">
                        <span className="filter-label-text-compact">Status</span>
                      </label>
                      <div className="filter-select-wrapper-compact status-multiselect-wrapper-compact" ref={statusDropdownRef}>
                        <div 
                          className="status-multiselect-trigger-compact"
                          onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              setStatusDropdownOpen(false);
                            }
                          }}
                        >
                          <span className="status-multiselect-display">
                            {filters.status && filters.status.length > 0
                              ? `${filters.status.length} selected`
                              : 'All Statuses'}
                          </span>
                          <ChevronDown size={14} className={`status-arrow ${statusDropdownOpen ? 'open' : ''}`} />
                        </div>
                        {statusDropdownOpen && (
                          <div className="status-multiselect-dropdown-compact">
                            {statusOptions.map(option => {
                              const isSelected = filters.status && filters.status.includes(option.value);
                              return (
                                <label 
                                  key={option.value}
                                  className="status-multiselect-option-compact"
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleStatusToggle(option.value)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <span>{option.label}</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Project Filter */}
                    {filterOptions.projects.length > 0 && (
                      <div className="filter-group-compact">
                        <label htmlFor="filter-project" className="filter-label-compact">
                          <span className="filter-label-text-compact">Project</span>
                        </label>
                        <div className="filter-select-wrapper-compact">
                          <select
                            id="filter-project"
                            value={filters.project}
                            onChange={(e) => setFilters(prev => ({ ...prev, project: e.target.value }))}
                            className="filter-select-compact"
                          >
                            <option value="">All Projects</option>
                            {filterOptions.projects.map(project => (
                              <option key={project.id} value={project.id}>
                                {project.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {/* Task Type Filter */}
                    {filterOptions.taskTypes.length > 0 && (
                      <div className="filter-group-compact">
                        <label htmlFor="filter-task-type" className="filter-label-compact">
                          <span className="filter-label-text-compact">Type</span>
                        </label>
                        <div className="filter-select-wrapper-compact">
                          <TaskTypeSelect
                            id="filter-task-type"
                            value={filters.taskType}
                            onChange={(e) => setFilters(prev => ({ ...prev, taskType: e.target.value }))}
                            className="filter-select-compact"
                            includeAllOption
                            options={filterOptions.taskTypes}
                          />
                        </div>
                      </div>
                    )}

                    {/* Feature Tag Filter */}
                    {filterOptions.featureTags.length > 0 && (
                      <div className="filter-group-compact">
                        <label htmlFor="filter-feature-tag" className="filter-label-compact">
                          <span className="filter-label-text-compact">Tag</span>
                        </label>
                        <div className="filter-select-wrapper-compact">
                          <select
                            id="filter-feature-tag"
                            value={filters.featureTag}
                            onChange={(e) => setFilters(prev => ({ ...prev, featureTag: e.target.value }))}
                            className="filter-select-compact"
                          >
                            <option value="">All Tags</option>
                            <option value="untagged">Untagged</option>
                            {filterOptions.featureTags.map(tag => (
                              <option key={tag.id} value={tag.id}>
                                {tag.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {/* Clear Filters Button */}
                    {(filters.searchQuery || filters.status.length > 0 || filters.project || filters.taskType || filters.featureTag) && (
                      <button
                        className="clear-filters-btn-compact"
                        onClick={clearFilters}
                        title="Clear all filters"
                      >
                        <span>Clear</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {availableTasks.length === 0 ? (
              <p className="no-tasks-message">No available tasks to add</p>
            ) : (
              <div className="task-list">
                {availableTasks.map(task => {
                  const selectable = isTaskSelectable(task);
                  return (
                    <div 
                      key={task.id}
                      className={`task-item ${selectedTaskId === task.id ? 'selected' : ''} ${!selectable ? 'disabled' : ''} clickable`}
                      onClick={(e) => {
                        // Don't trigger selection if clicking on interactive elements
                        if (e.target.closest('button') || e.target.closest('a')) {
                          return;
                        }
                        if (selectable) {
                          setSelectedTaskId(task.id);
                        }
                      }}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openTaskSummaryModal(task);
                      }}
                      title={!selectable ? `Cannot add ${task.status} tasks to work plan` : 'Click to select, double-click to view details'}
                    >
                      <div className="task-item-header">
                        <div className="flex items-center gap-2">
                          <TaskTypeIcon taskType={task.task_type} />
                          <h4>
                            {task.display_id && (
                              <span className="text-sm font-medium text-neutral-600 mr-2">
                                [{task.display_id}]
                              </span>
                            )}
                            {task.title}
                          </h4>
                          <ProjectBadge projectName={task.project?.name} />
                          {task.feature_tag && (
                            <FeatureTag 
                              name={task.feature_tag.name} 
                              color={task.feature_tag.color} 
                              size="small" 
                            />
                          )}
                          <TaskDueDate dueDate={task.feature_tag?.due_date} taskStatus={task.status} />
                        </div>
                        <div className="flex items-center gap-2">
                        <Badge variant={task.status.toLowerCase() === TASK_STATUS.IN_PROGRESS.toLowerCase() ? "warning" : undefined}>
                          {task.status}
                        </Badge>
                          <button
                            className="task-view-details-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              openTaskSummaryModal(task);
                            }}
                            title="View task details"
                          >
                            <Info size={16} />
                          </button>
                        </div>
                      </div>
                      {task.description && (
                        <p className="task-description">{task.description}</p>
                      )}
                      {((task.attachments && task.attachments.length > 0) || (task.work_plan_attachments && task.work_plan_attachments.length > 0)) && (
                        <div className="task-item-attachments">
                          <Paperclip size={14} className="task-item-attachments-icon" />
                          <span className="task-item-attachments-label">
                            {(() => {
                              const taskAttachments = task.attachments?.length || 0;
                              const workPlanAttachments = task.work_plan_attachments?.length || 0;
                              const total = taskAttachments + workPlanAttachments;
                              if (taskAttachments > 0 && workPlanAttachments > 0) {
                                return `${total} attachment${total !== 1 ? 's' : ''} (${taskAttachments} task, ${workPlanAttachments} work plan)`;
                              } else if (taskAttachments > 0) {
                                return `${taskAttachments} attachment${taskAttachments !== 1 ? 's' : ''}`;
                              } else {
                                return `${workPlanAttachments} work plan attachment${workPlanAttachments !== 1 ? 's' : ''}`;
                              }
                            })()}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="create-task-section">
            <h3 className="section-title">Create New Task</h3>
            <div className="task-form">
              <TextField label="Task title">
                <TextField.Input 
                  placeholder="Enter task title..."
                  value={newTask.title}
                  onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                />
              </TextField>
              <TextArea label="Task description">
                <TextArea.Input 
                  placeholder="Enter task description..."
                  value={newTask.description}
                  onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                />
              </TextArea>
            </div>
          </div>
        )}

        <div className="action-buttons">
          <Button 
            variant="neutral-tertiary"
            onClick={() => navigate('/dashboard')}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddTask}
            disabled={(mode === 'select' && !selectedTaskId) || 
                     (mode === 'create' && !newTask.title.trim())}
          >
            Add to Work Plan
          </Button>
        </div>
      </div>

      {/* Task Summary Modal */}
      <TaskSummaryModal
        task={selectedTaskForModal}
        isOpen={!!selectedTaskForModal}
        onClose={closeTaskSummaryModal}
        getTaskStatus={getTaskStatus}
        getStatusVariant={getStatusVariant}
        getTaskHistory={getTaskHistory}
        availableTags={[]}
        allTasks={allTasks}
        projectId={selectedTaskForModal?.project?.id}
        allowCrossProject={false}
        isManager={userIsManager}
        onTaskClick={openTaskSummaryModal}
      />
    </div>
  );
};

export default AddToWorkPlan; 